import { useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

const TIER_LIMITS: Record<string, number> = { free: 5, trial: 10, starter: 10, growth: 30 };

interface CreateTeamMemberData {
  business_id: string;
  first_name: string;
  last_name: string;
  role: string;
  employment_type: string;
  phone?: string;
  email?: string;
  hourly_rate?: number;
  // Build 45 sweep: activity_log.logged_by FKs public.users(id). Previously
  // seeded with business_id (wrong table entirely); the try/catch /* non-critical */
  // wrapper silently ate the FK rejection so no team-add events have been logged
  // from admin since this hook shipped. Caller must pass the current admin
  // user's public.users id.
  logged_by_user_id: string;
}

export function useCreateTeamMember() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const createTeamMember = useCallback(async (data: CreateTeamMemberData, tier: string): Promise<boolean> => {
    setLoading(true); setError(null);
    try {
      if (!data.first_name.trim()) { setError('First name is required'); setLoading(false); return false; }
      if (!data.last_name.trim()) { setError('Last name is required'); setLoading(false); return false; }

      // Check tier limit
      const limit = TIER_LIMITS[tier];
      if (limit) {
        const { count } = await supabase
          .from('users')
          .select('id', { count: 'exact', head: true })
          .eq('business_id', data.business_id)
          .eq('status', 'active');
        if ((count ?? 0) >= limit) {
          setError(`Team limit reached (${limit} on ${tier} plan). Upgrade to add more.`);
          setLoading(false);
          return false;
        }
      }

      const { data: newUser, error: err } = await supabase.from('users').insert({
        business_id: data.business_id,
        first_name: data.first_name.trim(),
        last_name: data.last_name.trim(),
        role: data.role || 'employee',
        employment_type: data.employment_type || 'employee',
        status: 'invited',
        phone: data.phone || null,
        email: data.email || null,
        hourly_rate: data.hourly_rate ?? null,
      }).select('id').maybeSingle();
      if (err) throw err;

      // Increment lifetime count (non-critical)
      try { await supabase.rpc('increment_lifetime_people_count', { biz_id: data.business_id }); } catch { /* RPC may not exist */ }

      // Activity log — entity_id is the new user's id; logged_by is the admin user doing the add.
      try { await supabase.from('activity_log').insert({ business_id: data.business_id, activity_type: 'team_member_added', description: `${data.first_name} ${data.last_name} added as ${data.employment_type}`, entity_type: 'user', entity_id: newUser?.id ?? null, logged_by: data.logged_by_user_id }); } catch { /* non-critical */ }

      setLoading(false);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add team member');
      setLoading(false);
      return false;
    }
  }, []);

  return { createTeamMember, loading, error };
}
